let _wasEligible = null
let _addedFromForm = false

window.BAO.eventBus.addEventListener(
  window.BAO.EVENTS.PUSH_CART.AFTER_ADD_FROM_FORM,
  () => { _addedFromForm = true },
)

class ComplimentaryGift extends window.BAO.CustomElement() {
  constructor () {
    super()

    this._submitting = false
    this.sections = window.BAO.sectionRenderer.pushCartSections
  }

  get config () {
    return {
      max: parseInt(this.dataset.max, 10) || 1,
      minPrice: parseInt(this.dataset.minPrice, 10) || 0,
      handles: (this.dataset.handles || '').split(',').map(h => h.trim()).filter(Boolean),
    }
  }

  get modal () {
    return this.externalEls.modal?.element
  }

  get banner () {
    return this.els.banner?.element
  }

  get isModalOpen () {
    return this.modal && this.modal.classList.contains('mod-Modal-active')
  }

  setupListeners () {
    super.setupListeners()

    this.onBannerClick = this.onBannerClick.bind(this)
    this.onCloseClick = this.onCloseClick.bind(this)
    this.onBackdropClick = this.onBackdropClick.bind(this)
    this.onKeyDown = this.onKeyDown.bind(this)
    this.onAddClick = this.onAddClick.bind(this)
    this.onRemoveClick = this.onRemoveClick.bind(this)
    if (this.els.banner?.exists) {
      this.listeners.add(this.els.banner.element, 'click', this.onBannerClick)
    }

    if (this.externalEls.close?.exists) {
      this.externalEls.close.elements.forEach(el => {
        this.listeners.add(el, 'click', this.onCloseClick)
      })
    }

    if (this.externalEls.modal?.exists) {
      this.listeners.add(this.externalEls.modal.element, 'click', this.onBackdropClick)
    }

    this.listeners.add(document, 'keydown', this.onKeyDown)

    if (this.externalEls.add?.exists) {
      this.externalEls.add.elements.forEach(el => {
        this.listeners.add(el, 'click', this.onAddClick)
      })
    }

    if (this.externalEls.remove?.exists) {
      this.externalEls.remove.elements.forEach(el => {
        this.listeners.add(el, 'click', this.onRemoveClick)
      })
    }

    this.syncModalState()
  }

  async syncModalState () {
    try {
      const state = await window.BAO.Cart.getCart()
      const { max, minPrice, handles } = this.config
      const subtotal = this.getSubtotal(state)
      const count = this.getComplimentaryCount(state, handles)

      const isEligible = subtotal >= minPrice && count < max

      const shouldOpen = (_wasEligible === false && isEligible) ||
        (_addedFromForm && isEligible)

      _addedFromForm = false

      if (shouldOpen) {
        this.openModal()
      }

      _wasEligible = isEligible
      this.updateModalState(state)
    } catch (err) {
      console.error('Failed to sync modal')
    }
  }

  openModal () {
    if (this.modal) {
      this.modal.classList.add('mod-Modal-active')
    }
  }

  closeModal () {
    if (this.modal) {
      this.modal.classList.remove('mod-Modal-active')
    }
  }

  onBannerClick (e) {
    e.preventDefault()
    this.openModal()
  }

  onCloseClick (e) {
    e.preventDefault()
    this.closeModal()
  }

  onBackdropClick (e) {
    if (!e.target.closest('[data-modal-body]')) {
      this.closeModal()
    }
  }

  onKeyDown (e) {
    if (e.key === 'Escape' && this.isModalOpen) {
      this.closeModal()
    }
  }

  async onAddClick (e) {
    e.preventDefault()

    if (this._submitting) return
    this._submitting = true

    const button = e.currentTarget
    const variantId = button.dataset.variantId

    if (!variantId) {
      this._submitting = false
      return
    }

    button.disabled = true

    try {
      await window.BAO.Cart.addItem(
        {
          id: parseInt(variantId, 10),
          quantity: 1,
          properties: {
            _complimentary: 'true',
            _complimentary_max: String(this.config.max),
          },
        },
        {
          sections: this.sections.map(s => s.section),
        },
      )

      const state = await window.BAO.Cart.getCart()
      this.updateModalState(state)
    } catch (err) {
      console.error('Complimentary: Failed to add item', err)
      button.disabled = false
    } finally {
      this._submitting = false
    }
  }

  async onRemoveClick (e) {
    e.preventDefault()

    if (this._submitting) return
    this._submitting = true

    const button = e.currentTarget
    const key = button.dataset.key

    if (!key) {
      this._submitting = false
      return
    }

    button.disabled = true

    try {
      await window.BAO.Cart.removeItem(key, {
        sections: this.sections.map(s => s.section),
      })

      const state = await window.BAO.Cart.getCart()
      this.updateModalState(state)
    } catch (err) {
      console.error('Complimentary: Failed to remove item', err)
      button.disabled = false
    } finally {
      this._submitting = false
    }
  }

  updateModalState (state) {
    if (!this.modal) return

    const { max, handles } = this.config
    const items = state.items || []
    const count = this.getComplimentaryCount(state, handles)
    const isMaxed = count >= max

    const products = this.modal.querySelectorAll('[data-complimentary-product]')

    products.forEach(productEl => {
      const handle = productEl.dataset.handle
      const addBtn = productEl.querySelector('[data-complimentary-gift-external-el="add"]')
      const removeBtn = productEl.querySelector('[data-complimentary-gift-external-el="remove"]')

      if (!addBtn || !removeBtn) return

      const cartItem = items.find(item => {
        const props = item.properties || {}
        return item.handle === handle && props._complimentary === 'true'
      })

      if (cartItem) {
        addBtn.setAttribute('aria-hidden', 'true')
        addBtn.disabled = true
        removeBtn.setAttribute('aria-hidden', 'false')
        removeBtn.disabled = false
        removeBtn.dataset.key = cartItem.key
      } else {
        addBtn.setAttribute('aria-hidden', 'false')
        addBtn.disabled = isMaxed
        removeBtn.setAttribute('aria-hidden', 'true')
        removeBtn.disabled = true
      }
    })

    if (this.isModalOpen && isMaxed) {
      this.closeModal()
    }
  }

  getSubtotal (state) {
    if (!state?.items) return 0

    let subtotal = 0
    for (const item of state.items) {
      const props = item.properties || {}
      if (props._complimentary === 'true' || props._gwp === 'true') continue
      subtotal += item.final_line_price
    }
    return subtotal
  }

  getComplimentaryCount (state, handles) {
    if (!state?.items) return 0

    return state.items.filter(item => {
      const props = item.properties || {}
      return props._complimentary === 'true' && handles.includes(item.handle)
    }).length
  }
}

if (!customElements.get('complimentary-gift')) {
  customElements.define('complimentary-gift', ComplimentaryGift)
}
